// src/sync/supabaseSyncAdapter.ts

/**
 * Supabase Sync Adapter - the real cloud side of project-level sync.
 *
 * Implements SyncRemoteAdapter against public.project_sync_records (owner-scoped
 * RLS). One row per project holds the schema fingerprint, version, last-modified
 * stamp, and the full project payload as jsonb. The cloud sync engine calls
 * fetch/push; this module is the only place that knows the table shape.
 *
 * Security: every write is stamped with the authenticated user's id so RLS keeps
 * each user's project records isolated. A push without an authenticated session
 * fails closed (no anonymous writes).
 */

import { supabase, isSupabaseConfigured } from "@/integrations/supabase/client";
import type { CloudProjectRecord, SyncRemoteAdapter } from "@/sync/cloudSyncEngine";
import { logger } from "@/system/logger";

const TABLE = "project_sync_records";

interface SyncRow {
  project_id:         string;
  schema_fingerprint: string;
  schema_version:     number;
  last_modified_at:   number | string;
  last_modified_by:   string;
  device_id:          string;
  data:               CloudProjectRecord["data"];
}

function rowToRecord(row: SyncRow): CloudProjectRecord {
  return {
    projectId:         row.project_id,
    schemaFingerprint: row.schema_fingerprint,
    schemaVersion:     row.schema_version,
    lastModifiedAt:    Number(row.last_modified_at),
    lastModifiedBy:    row.last_modified_by,
    deviceId:          row.device_id,
    data:              row.data,
  };
}

export function createSupabaseSyncAdapter(): SyncRemoteAdapter {
  return {
    async fetch(projectId: string): Promise<CloudProjectRecord | null> {
      if (!isSupabaseConfigured()) return null;

      // project_sync_records is created by migration 20260627140000 but is not
      // yet in the generated Supabase types, so the typed client resolves its
      // columns to `never`. Cast the builder (same pattern as
      // collectionSyncAdapter for nuvra_collection_records); the query is real.
      const { data, error } = await (supabase as any).from(TABLE)
        .select("project_id, schema_fingerprint, schema_version, last_modified_at, last_modified_by, device_id, data")
        .eq("project_id", projectId)
        .maybeSingle();

      if (error) {
        logger.error("[SupabaseSyncAdapter] fetch failed", { projectId, error: error.message });
        throw new Error(`Cloud fetch failed: ${error.message}`);
      }
      return data ? rowToRecord(data as SyncRow) : null;
    },

    async push(record: CloudProjectRecord): Promise<void> {
      if (!isSupabaseConfigured()) throw new Error("Supabase not configured");

      // Stamp the owning user for RLS. Fail closed without a session.
      const { data: auth } = await supabase.auth.getUser();
      const userId = auth?.user?.id;
      if (!userId) throw new Error("Cannot push: no authenticated session");

      const { error } = await (supabase as any).from(TABLE)
        .upsert(
          {
            project_id:         record.projectId,
            user_id:            userId,
            schema_fingerprint: record.schemaFingerprint,
            schema_version:     record.schemaVersion,
            last_modified_at:   record.lastModifiedAt,
            last_modified_by:   record.lastModifiedBy,
            device_id:          record.deviceId,
            data:               record.data,
            updated_at:         new Date().toISOString(),
          },
          { onConflict: "project_id" },
        );

      if (error) {
        logger.error("[SupabaseSyncAdapter] push failed", { projectId: record.projectId, error: error.message });
        throw new Error(`Cloud push failed: ${error.message}`);
      }
    },
  };
}
